import { useCallback, useEffect, useRef, useState } from 'react';
import { Building2, Globe, MapPin, Plus, Trash2, X, Search, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import Button from '../ui/Button';
import cultivarService from '../../services/cultivar';
import organizationService from '../../services/organization';

const COUNTRIES = [
  'Argentina', 'Australia', 'Austria', 'Belgium', 'Brazil', 'Canada', 'Chile',
  'China', 'Czech Republic', 'Denmark', 'Egypt', 'Finland', 'France', 'Germany',
  'Greece', 'Hungary', 'India', 'Indonesia', 'Ireland', 'Israel', 'Italy',
  'Japan', 'Kenya', 'Malaysia', 'Mexico', 'Morocco', 'Netherlands',
  'New Zealand', 'Norway', 'Pakistan', 'Peru', 'Philippines', 'Poland',
  'Portugal', 'Romania', 'Saudi Arabia', 'Singapore', 'South Africa',
  'South Korea', 'Spain', 'Sweden', 'Switzerland', 'Thailand', 'Turkey',
  'Ukraine', 'United Arab Emirates', 'United Kingdom', 'United States',
  'Vietnam',
];

interface LicensorRow {
  id: number;
  licensor_org_id: number;
  licensor_org_name: string;
  region: string | null;
  country: string | null;
  notes: string | null;
  granted_at: string;
}

interface Props {
  cultivarId: number;
  /** Can the viewer add new grants? True if they're the creator (no grants yet) OR an existing licensor. */
  canManage: boolean;
  onChange?: () => void;
}

interface OrgResult {
  id: number;
  name: string;
  stakeholder_types: string[];
}

export default function LicensorsPanel({ cultivarId, canManage, onChange }: Props) {
  const [rows, setRows] = useState<LicensorRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // form state
  const [orgQuery, setOrgQuery] = useState('');
  const [orgResults, setOrgResults] = useState<OrgResult[]>([]);
  const [orgOpen, setOrgOpen] = useState(false);
  const [orgLoading, setOrgLoading] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState<number | undefined>();
  const [selectedOrgName, setSelectedOrgName] = useState('');
  const [region, setRegion] = useState('');
  const [country, setCountry] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [orgCountry, setOrgCountry] = useState<string>('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    organizationService.getCurrentOrganization()
      .then(res => {
        if (res.success && res.data?.country) {
          setOrgCountry(res.data.country);
          setCountry(res.data.country);
        }
      })
      .catch(() => { /* ignore — leave default empty */ });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await cultivarService.getCultivarLicensors(cultivarId);
      if (res.success && res.data) setRows(res.data as LicensorRow[]);
    } finally {
      setLoading(false);
    }
  }, [cultivarId]);

  useEffect(() => { load(); }, [load]);

  const searchOrgs = async (q: string) => {
    if (q.length < 2) { setOrgResults([]); return; }
    setOrgLoading(true);
    try {
      const res = await organizationService.searchOrganizations({ name: q });
      if (res.success && res.data) setOrgResults(res.data as OrgResult[]);
    } catch { /* ignore */ } finally {
      setOrgLoading(false);
    }
  };

  const onOrgQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const q = e.target.value;
    setOrgQuery(q);
    setOrgOpen(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchOrgs(q), 300);
  };

  const pickOrg = (org: OrgResult) => {
    setSelectedOrgId(org.id);
    setSelectedOrgName(org.name);
    setOrgOpen(false);
    setOrgQuery('');
  };

  const resetForm = () => {
    setSelectedOrgId(undefined);
    setSelectedOrgName('');
    setRegion('');
    setCountry(orgCountry);
    setNotes('');
    setError(null);
    setShowForm(false);
  };

  const submit = async () => {
    if (!selectedOrgId) { setError('Pick a licensor organisation'); return; }
    if (!region && !country) { setError('At least one of Region or Country is required (use both blank only for truly global rights)'); /* warn but allow */ }
    setSubmitting(true);
    setError(null);
    try {
      const res = await cultivarService.addCultivarLicensor(cultivarId, {
        licensor_org_id: selectedOrgId,
        region: region || null,
        country: country || null,
        notes: notes || null
      });
      if (res.success) {
        resetForm();
        await load();
        onChange?.();
      } else {
        setError((res as any)?.error?.message || 'Failed to add licensor');
      }
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || e.message || 'Failed to add licensor');
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (row: LicensorRow) => {
    if (!confirm(`Remove ${row.licensor_org_name} as licensor for ${row.region || 'any region'} / ${row.country || 'any country'}?`)) return;
    try {
      await cultivarService.removeCultivarLicensor(cultivarId, row.id);
      await load();
      onChange?.();
    } catch (e: any) {
      alert(e?.response?.data?.error?.message || 'Failed to remove');
    }
  };

  const scopeLabel = (r: LicensorRow) => {
    if (!r.region && !r.country) return <span className="text-amber-700 flex items-center gap-1"><Globe className="h-3 w-3" /> Global</span>;
    const parts = [r.region, r.country].filter(Boolean);
    return <span className="text-neutral-700 flex items-center gap-1"><MapPin className="h-3 w-3" /> {parts.join(', ')}</span>;
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Licensors (region-scoped)</CardTitle>
        {canManage && !showForm && (
          <Button variant="primary" size="sm" onClick={() => setShowForm(true)}>
            <Plus className="h-4 w-4" /> Add Licensor
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="text-sm text-neutral-400 flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading licensors…
          </div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-neutral-500 bg-amber-50 border border-amber-200 rounded p-3">
            No licensors have been set for this cultivar yet.
            {canManage && ' Use "Add Licensor" to record who holds licensor rights and for which region/country.'}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-neutral-500">
              <tr>
                <th className="text-left py-1">Organisation</th>
                <th className="text-left py-1">Scope</th>
                <th className="text-left py-1">Notes</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-t">
                  <td className="py-2">
                    <span className="flex items-center gap-2 font-medium">
                      <Building2 className="h-4 w-4 text-neutral-400" /> {r.licensor_org_name}
                    </span>
                  </td>
                  <td className="py-2">{scopeLabel(r)}</td>
                  <td className="py-2 text-neutral-500">{r.notes || '—'}</td>
                  <td className="py-2 text-right">
                    {canManage && (
                      <button onClick={() => remove(r)} className="text-red-500 hover:text-red-700" title="Remove">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {showForm && (
          <div className="border rounded-lg p-4 bg-neutral-50 space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-sm">Add licensor grant</h4>
              <button onClick={resetForm} className="text-neutral-400 hover:text-neutral-600"><X className="h-4 w-4" /></button>
            </div>

            <div>
              <label className="block text-xs text-neutral-500 mb-1">Organisation</label>
              {selectedOrgId && selectedOrgName ? (
                <div className="flex items-center justify-between px-3 py-2 bg-white border border-blue-200 rounded">
                  <span className="text-sm font-medium">{selectedOrgName}</span>
                  <button onClick={() => { setSelectedOrgId(undefined); setSelectedOrgName(''); }} className="text-neutral-400">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <div className="relative">
                    <input
                      type="text"
                      value={orgQuery}
                      onChange={onOrgQueryChange}
                      onFocus={() => { setOrgOpen(true); if (orgQuery.length >= 2) searchOrgs(orgQuery); }}
                      onBlur={() => setTimeout(() => setOrgOpen(false), 200)}
                      placeholder="Search organisations…"
                      className="w-full pl-9 pr-3 py-2 border border-neutral-300 rounded text-sm"
                    />
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400">
                      {orgLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                    </div>
                  </div>
                  {orgOpen && orgResults.length > 0 && (
                    <div className="absolute z-10 top-full left-0 right-0 mt-1 bg-white border border-neutral-200 rounded shadow max-h-52 overflow-y-auto">
                      {orgResults.map(org => (
                        <button
                          key={org.id}
                          type="button"
                          className="w-full text-left px-3 py-2 hover:bg-neutral-50 text-sm"
                          onMouseDown={(e) => { e.preventDefault(); pickOrg(org); }}
                        >
                          <p className="font-medium">{org.name}</p>
                          {org.stakeholder_types?.length > 0 && (
                            <p className="text-xs text-neutral-500">{org.stakeholder_types.join(', ')}</p>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-neutral-500 mb-1">Region</label>
                <input type="text" value={region} onChange={e => setRegion(e.target.value)}
                  placeholder="e.g. South Australia" className="w-full border rounded px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs text-neutral-500 mb-1">Country</label>
                <select value={country} onChange={e => setCountry(e.target.value)}
                  className="w-full border rounded px-3 py-2 text-sm bg-white">
                  <option value="">— Any country —</option>
                  {orgCountry && (
                    <optgroup label="Your organisation">
                      <option value={orgCountry}>{orgCountry}</option>
                    </optgroup>
                  )}
                  <optgroup label="All countries">
                    {COUNTRIES.filter(c => c !== orgCountry).map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </optgroup>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs text-neutral-500 mb-1">Notes</label>
              <input type="text" value={notes} onChange={e => setNotes(e.target.value)}
                className="w-full border rounded px-3 py-2 text-sm" />
            </div>
            <p className="text-xs text-neutral-500">
              Leaving both Region and Country blank grants <strong>global</strong> licensor rights — use sparingly.
            </p>

            {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>}

            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={resetForm}>Cancel</Button>
              <Button variant="primary" size="sm" onClick={submit} disabled={submitting || !selectedOrgId}>
                {submitting ? 'Saving…' : 'Grant licensor rights'}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
